import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { RegistrationStep } from '../../../../../Utils/constants';
import useFitnessVillageCopy, {
  useLocalizeMessage,
} from '../../../../../hooks/useFitnessVillageCopy';
import useDfcSurface from '../../../../../hooks/useDfcSurface';
import useDfcVillageDesktop from '../../../../../hooks/useDfcVillageDesktop';
import { Eyebrow } from '../../../../shared/dfcVillage/style';
import { useAuthenticationForm } from '../../hooks/useAuthenticationForm';
import { AuthPanelLayout, IAuthenticationFormProps } from '../../Types';
import AuthenticationFormTabs from './AuthenticationFormTabs';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import SocialAuthentication from './SocialAuthentication';
import {
  AuthError,
  AuthErrorList,
  CardShell,
  CloseButton,
  InlineShell,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  SheetDrawer,
} from './style';

interface Props extends IAuthenticationFormProps {
  /** Overrides the sheet's default close (navigate back) — used when hosted as an overlay. */
  onClose?: () => void;
}

/**
 * DFC Fitness Village authentication panel — a skin over the app's existing auth machinery
 * (`useAuthenticationForm` + `useLoginForm`, unchanged). One variant, three presentations:
 * a bottom sheet on mobile, a centred card on desktop (both reached from the header's
 * Sign in / Create account via `/Register`), and an inline block for the court auth gate.
 *
 * No new validation, no new auth calls, no passwordless path — the fidelity decision is email +
 * password (plan §7). Errors are whatever `useAuthenticationForm` already surfaces on the form.
 */
const AuthenticationFormDfcVillage: React.FC<Props> = ({
  redirectToCompleteProfile,
  currentStep,
  redirectToCalendar,
  setCurrentStep,
  layout,
  onClose,
}) => {
  const surface = useDfcSurface();
  const isDesktop = useDfcVillageDesktop();
  const navigate = useNavigate();
  const authCopy = useFitnessVillageCopy().auth;
  const localize = useLocalizeMessage();

  const {
    signUpForm,
    isLoading,
    loginWithSocialMedia,
    isSocialLoading,
    signUpWithEmailAndPassword,
    signInWithEmailAndPassword,
    supportSocialAuthentication,
  } = useAuthenticationForm(currentStep, redirectToCompleteProfile, redirectToCalendar);

  const isLogin = currentStep === RegistrationStep.Login;
  const copy = isLogin ? authCopy.signIn : authCopy.signUp;

  // A sheet reached via /Register has no host to close it, so it falls back to the previous page.
  const resolvedLayout: AuthPanelLayout = layout ?? (isDesktop ? 'card' : 'sheet');
  const handleClose = onClose ?? (() => navigate(-1));

  const errors = signUpForm.formState.errors;
  const errorMessages = (
    [errors.email?.message, errors.password?.message].filter(Boolean) as string[]
  ).map(localize);

  const body = (
    <PanelBody inline={resolvedLayout === 'inline'}>
      <PanelHeader>
        <Eyebrow tone={surface.accentText}>{authCopy.eyebrow}</Eyebrow>
        <AuthenticationFormTabs
          surface={surface}
          formType={currentStep}
          onChangeFormHandler={(_, value) => setCurrentStep(value)}
        />
        <PanelTitle surface={surface}>{copy.title}</PanelTitle>
        <PanelSubtitle surface={surface}>{copy.body}</PanelSubtitle>
      </PanelHeader>

      {errorMessages.length > 0 && (
        <AuthError surface={surface} role="alert" data-testid="auth-error">
          <ErrorOutlineIcon />
          <AuthErrorList>
            {errorMessages.map(message => (
              <li key={message}>{message}</li>
            ))}
          </AuthErrorList>
        </AuthError>
      )}

      {isLogin ? (
        <LoginForm
          surface={surface}
          form={signUpForm}
          onSubmit={signInWithEmailAndPassword}
          isLoading={isLoading}
        />
      ) : (
        <SignupForm
          surface={surface}
          form={signUpForm}
          onSubmit={signUpWithEmailAndPassword}
          isLoading={isLoading}
        />
      )}

      {supportSocialAuthentication && (
        <SocialAuthentication
          surface={surface}
          loginWithSocialMedia={loginWithSocialMedia}
          isSocialLoading={isSocialLoading}
        />
      )}
    </PanelBody>
  );

  if (resolvedLayout === 'inline') {
    return <InlineShell data-testid="auth-panel-inline">{body}</InlineShell>;
  }

  if (resolvedLayout === 'card') {
    return (
      <CardShell surface={surface} data-testid="auth-panel-card">
        {body}
      </CardShell>
    );
  }

  return (
    <SheetDrawer
      surface={surface}
      anchor="bottom"
      open
      onClose={handleClose}
      transitionDuration={0}
      data-testid="auth-panel-sheet"
    >
      <CloseButton surface={surface} aria-label={authCopy.close} onClick={handleClose}>
        <CloseIcon />
      </CloseButton>
      {body}
    </SheetDrawer>
  );
};

export default AuthenticationFormDfcVillage;
